/**
 * Cross-window sync for the keynote.
 * Uses BroadcastChannel — same-origin, no backend, works across tabs/windows
 * on the same browser/device. Falls back to localStorage events if needed.
 *
 * Channel messages:
 *   { type: "STATE", index, presenterOpen, ts }      // master broadcasts truth
 *   { type: "REQUEST_STATE" }                          // new window asks for current state
 *   { type: "GO", index }                              // any window requests jump
 *   { type: "STEP", dir: 1 | -1 }                      // any window requests next/prev
 *   { type: "TOGGLE_PRESENTER" }                       // any window asks to toggle overview
 */

const CHANNEL_NAME = "keynote-sync-v1";

let channel = null;

function getChannel() {
  if (channel) return channel;
  if (typeof window === "undefined") return null;
  if (typeof BroadcastChannel === "undefined") {
    // Older browsers: fall back to a localStorage shim
    channel = createLocalStorageChannel();
  } else {
    channel = new BroadcastChannel(CHANNEL_NAME);
  }
  return channel;
}

function createLocalStorageChannel() {
  const listeners = new Set();
  const KEY = "__keynote_sync_event__";
  window.addEventListener("storage", (e) => {
    if (e.key !== KEY || !e.newValue) return;
    try {
      const msg = JSON.parse(e.newValue);
      listeners.forEach((l) => l({ data: msg }));
    } catch {
      /* ignore */
    }
  });
  return {
    postMessage: (msg) => {
      try {
        window.localStorage.setItem(KEY, JSON.stringify({ ...msg, _t: Date.now() }));
      } catch {
        /* ignore */
      }
    },
    addEventListener: (type, handler) => {
      if (type === "message") listeners.add(handler);
    },
    removeEventListener: (type, handler) => {
      if (type === "message") listeners.delete(handler);
    },
    close: () => listeners.clear(),
  };
}

export function postSync(message) {
  const ch = getChannel();
  if (!ch) return;
  try {
    ch.postMessage(message);
  } catch {
    /* ignore */
  }
}

export function subscribeSync(handler) {
  const ch = getChannel();
  if (!ch) return () => {};
  const wrapped = (e) => handler(e.data);
  ch.addEventListener("message", wrapped);
  return () => {
    try {
      ch.removeEventListener("message", wrapped);
    } catch {
      /* ignore */
    }
  };
}
